import * as React from "react";
import { css, useTheme } from "@emotion/react";
import { StaticImage } from "gatsby-plugin-image";

import Navigation from "../Navigation";
import Footer from "../Footer";
import TableList from "../TableList";
import Seo from "../Seo";
import Player from "../Player";
import { isBrowser } from "../utils";
import ArrowRight from "../ArrowRight";

import phrases from "../constants/glassGoPhrases";
import audio from "../../static/glassgo.mp3";
import audioEn from "../../static/glassgo_en.mp3";

const innerContainerStyle = css`
  padding: 0 20px;
`;

const riddlesListStyle = (theme) => css`
  ${theme.grids.triple};
  width: 100%;
  margin: 0;
  padding: 0;
  text-align: center;
  list-style-type: none;
  margin-top: 20px;
  column-gap: 10px;
  row-gap: 10px;

  a {
    color: inherit;
    text-decoration: none;
    border: 1px solid currentColor;
    display: block;
    padding: 5px;

    span {
      display: inline-block;
      margin-right: 15px;
    
    }
  }
`;

const openMapButtonStyle = (theme) => css`
  ${theme.emotionTypography.smallLead};
  color: inherit;
  text-decoration: none;
  border: 1px solid currentColor;
  padding: 5px 15px;
  display: inline-block;
  margin-top: 20px;
`;

const authorsSectionStyle = (theme) => css`
  ${innerContainerStyle};
  background-color: ${theme.colors.darkGrey};
  color: ${theme.colors.whiteText};
  padding-top: 50px;
  padding-bottom: 50px;
  ${theme.grids.fortyToOne};
  column-gap: 40px;
`;

const authorsLead = (theme) => css`
  ${theme.emotionTypography.smallLead};
  margin-bottom: 150px;
`;

const constainerStyle = (theme) => css`
  min-height: 100vh;

  section {
    scroll-margin-top: 75px;
  }
`;

const mapContainerStyle = (theme) => css`
  ${innerContainerStyle};
  background-color: ${theme.colors.darkGrey};
  color: ${theme.colors.whiteText};
  padding-bottom: 50px;

  h1 {
    ${theme.emotionTypography.lead};
    margin: 0;
    padding-top: 20px;
    line-height: 1em;
  }
`;

const introductionStyle = (theme) => css`
  ${theme.emotionTypography.smallLead};
  text-indent: 3em;
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 20px;
`;

const descriptionContainerStyle = (theme) => css`
  ${theme.grids.split}
  column-gap: 50px;
  row-gap: 50px;
  hyphens: auto;
  padding-bottom: 50px;
  margin: 0 20px;
  ${theme.breakpoints.mobile} {
    row-gap: 20px;
  }
`;

const sectionContent = css`
  max-width: 990px;
  margin: 0 auto;
`;

const mapContainerContentStyle = (theme) => css`
  ${theme.grids.split};
  align-items: end;
`;

const mapDraftStyle = css`
  max-width: 100%;
  display: block;
`;

const mapContentParagraph = css`
  max-width: 500px;
`;

const mapTopTextStyle = css`
  ${mapContentParagraph};
  text-align: right;
`;

const mapColumnStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const autorTile = css`
  position: relative;
  padding-bottom: 100px;
  img {
    max-width: 100%;
    object-fit: cover;
    display: block;
  }
`;

const authorMetaStyle = (theme) => css`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 5px;
  min-height: 100px;
  background-color: ${theme.colors.darkGrey};
  p {
    margin: 0;
  }
`;

const authorNameStyle = css`
  font-size: 1.2em;
`;

const authorsPhotoContainerStyle = (theme) => css`
  ${theme.grids.triple};
`;

const authorRoleStyle = css`
  font-size: 0.8em;
  padding-right: 15px;
`;

const GlassGo2Page = ({ pageContext }) => {
  const language = pageContext?.language || "pl";
  const otherLanguageUrl = language === "pl" ? "/en/glass-go" : "/glass-go";
  const theme = useTheme();
  return (
    <>
      <Seo
        language={language}
        title={language === "pl" ? "GlassGo2" : "GlassGo2"}
      />
      <div css={constainerStyle}>
        <Navigation
          foreground={theme.colors.whiteText}
          background={theme.colors.darkGrey}
          language={language}
          languageUrl={otherLanguageUrl}
        >
          <ul>
            <li>
              <a href="#mapa">{phrases[42][language]}</a>
            </li>
            <li>
              <a href="#opis">{phrases[43][language]}</a>
            </li>
            <li>
              <a href="#autorzy">{phrases[44][language]}</a>
            </li>
          </ul>
        </Navigation>
        <section css={mapContainerStyle} id="mapa">
          <h1>
            GlassGo<sup>2</sup>
          </h1>
          <div css={mapContainerContentStyle}>
            <div>
              {isBrowser() && (
                <Player
                  file={language === "pl" ? audio : audioEn}
                  language={language}
                />
              )}
              <p css={mapContentParagraph}>{phrases[1][language]}</p>
            </div>
            <div css={mapColumnStyle}>
              <p css={mapTopTextStyle}>{phrases[0][language]}</p>
              <a href="/map_full.png" target="_blank">
                <StaticImage
                  src={"../images/mapa-draft.png"}
                  alt="Mapa"
                  css={mapDraftStyle}
                />
              </a>
              <ul css={riddlesListStyle}>
                <li>
                  <a
                    href="https://www.google.com/maps/place/51%C2%B006'08.5%22N+17%C2%B001'42.2%22E/@51.1023703,17.0262083,17z/data=!3m1!4b1!4m5!3m4!1s0x0:0x0!8m2!3d51.102367!4d17.028397"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                  >
                    <span>{phrases[50][language]} 1</span>
                    <ArrowRight height={15} aria-hidden="true" />
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.google.com/maps/place/51%C2%B006'08.0%22N+17%C2%B001'43.4%22E/@51.1022223,17.0265403,17z/data=!3m1!4b1!4m5!3m4!1s0x0:0x0!8m2!3d51.102219!4d17.028729"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                  >
                    <span>{phrases[50][language]} 2</span>
                    <ArrowRight height={15} aria-hidden="true" />
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.google.com/maps/place/51%C2%B006'07.0%22N+17%C2%B001'49.1%22E/@51.1019413,17.0281213,17z/data=!3m1!4b1!4m5!3m4!1s0x0:0x0!8m2!3d51.101938!4d17.03031"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                  >
                    <span>{phrases[50][language]} 3</span>
                    <ArrowRight height={15} aria-hidden="true" />
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.google.com/maps/place/51%C2%B006'07.8%22N+17%C2%B001'49.4%22E/@51.1021563,17.0282063,17z/data=!3m1!4b1!4m5!3m4!1s0x0:0x0!8m2!3d51.102153!4d17.030395"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                  >
                    <span>{phrases[50][language]} 4</span>
                    <ArrowRight height={15} aria-hidden="true" />
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.google.com/maps/place/51%C2%B006'12.1%22N+17%C2%B001'51.5%22E/@51.1033643,17.0287943,17z/data=!3m1!4b1!4m5!3m4!1s0x0:0x0!8m2!3d51.103361!4d17.030983"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                  >
                    <span>{phrases[50][language]} 5</span>
                    <ArrowRight height={15} aria-hidden="true" />
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.google.com/maps/place/51%C2%B006'12.7%22N+17%C2%B001'47.9%22E/@51.1035303,17.0277763,17z/data=!3m1!4b1!4m5!3m4!1s0x0:0x0!8m2!3d51.103527!4d17.029965"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                  >
                    <span>{phrases[50][language]} 6</span>
                    <ArrowRight height={15} aria-hidden="true" />
                  </a>
                </li>
              </ul>
              {/* <a href="/map_full.png" target="_blank" css={openMapButtonStyle}>
                {phrases[49][language]}
              </a> */}
            </div>
          </div>
        </section>
        <section id="opis">
          <p css={introductionStyle}>{phrases[2][language]}</p>
          <div css={sectionContent}>
            <div css={descriptionContainerStyle}>
              <p>{phrases[3][language]}</p>
              <p>{phrases[4][language]}</p>
              <TableList>
                <h2>{phrases[5][language]}</h2>
                <ol>
                  <li>
                    <span>{phrases[6][language]}</span>
                    <span>{phrases[7][language]}</span>
                  </li>
                  <li>
                    <span>{phrases[8][language]}</span>
                    <span>{phrases[9][language]}</span>
                  </li>
                </ol>
              </TableList>
              <TableList>
                <h2>{phrases[10][language]}</h2>
                <ol>
                  <li>
                    <span>{phrases[11][language]}</span>
                    <span>{phrases[12][language]}</span>
                  </li>
                  <li>
                    <span>{phrases[13][language]}</span>
                    <span>{phrases[14][language]}</span>
                  </li>
                  <li>
                    <span>{phrases[15][language]}</span>
                    <span>{phrases[16][language]}</span>
                  </li>
                </ol>
              </TableList>
            </div>
          </div>
        </section>
        <section css={authorsSectionStyle} id="autorzy">
          <div>
            <p css={authorsLead}>{phrases[17][language]}</p>
            <TableList>
              {/* <h2>Autorzy</h2> */}
              <ol>
                <li>
                  <span>{phrases[18][language]} </span>
                  <span>{phrases[19][language]}</span>
                </li>
                <li>
                  <span>{phrases[20][language]}</span>
                  <span>{phrases[21][language]}</span>
                </li>
                <li>
                  <span>{phrases[22][language]}</span>
                  <span>{phrases[23][language]}</span>
                </li>
                <li>
                  <span>{phrases[24][language]}</span>
                  <span>{phrases[25][language]}</span>
                </li>
                <li>
                  <span>{phrases[26][language]}</span>
                  <span>{phrases[27][language]}</span>
                </li>
                <li>
                  <span>{phrases[28][language]}</span>
                  <span>{phrases[29][language]}</span>
                </li>
                <li>
                  <span>{phrases[46][language]}</span>
                  <span>{phrases[48][language]}</span>
                </li>
                <li>
                  <span>{phrases[45][language]}</span>
                  <span>{phrases[47][language]}</span>
                </li>
              </ol>
            </TableList>
          </div>
          <div css={authorsPhotoContainerStyle}>
            <div css={autorTile}>
              <StaticImage
                src={"../images/barbara_celucka.jpg"}
                alt="Barbara Celucka"
              />
              <div css={authorMetaStyle}>
                <p css={authorNameStyle}>{phrases[30][language]}</p>
                <p css={authorRoleStyle}>{phrases[31][language]}</p>
              </div>
            </div>
            <div css={autorTile}>
              <StaticImage
                src={"../images/dominika_drozdowska.jpg"}
                alt="Dominika Drozdowska"
              />
              <div css={authorMetaStyle}>
                <p css={authorNameStyle}>{phrases[32][language]}</p>
                <p css={authorRoleStyle}>{phrases[33][language]}</p>
              </div>
            </div>
            <div css={autorTile}>
              <StaticImage
                src={"../images/agata_grzych.jpg"}
                alt="Agata Grzych"
              />
              <div css={authorMetaStyle}>
                <p css={authorNameStyle}>{phrases[34][language]}</p>
                <p css={authorRoleStyle}>{phrases[35][language]}</p>
              </div>
            </div>
            <div css={autorTile}>
              <StaticImage
                src={"../images/iwona_kaluza.jpg"}
                alt="Iwona Kałuża"
              />
              <div css={authorMetaStyle}>
                <p css={authorNameStyle}>{phrases[36][language]}</p>
                <p css={authorRoleStyle}>{phrases[37][language]}</p>
              </div>
            </div>
            <div css={autorTile}>
              <StaticImage
                src={"../images/karina_marusinska.jpg"}
                alt="Karina Marusińska"
              />
              <div css={authorMetaStyle}>
                <p css={authorNameStyle}>{phrases[38][language]}</p>
                <p css={authorRoleStyle}>{phrases[39][language]}</p>
              </div>
            </div>
            <div css={autorTile}>
              <StaticImage
                src={"../images/alicja_patanowska.jpg"}
                alt="Alicja Patanowska"
              />
              <div css={authorMetaStyle}>
                <p css={authorNameStyle}>{phrases[40][language]}</p>
                <p css={authorRoleStyle}>{phrases[41][language]}</p>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer language={language} />
    </>
  );
};

export default GlassGo2Page;
